import Image from "next/image";
import Link from "next/link";

interface ComparisonHeroProps {
  trustBadge: string;
  title: string;
  titleHighlight: string;
  description: string;
  competitorLogo?: { src: string; alt: string };
}

const DOT_POSITIONS = [
  "left-[18%] top-[15%] h-2 w-2",
  "right-[22%] top-[30%] h-1.5 w-1.5",
  "left-[12%] bottom-[35%] h-2.5 w-2.5",
  "right-[15%] bottom-[18%] h-2 w-2",
  "left-[35%] top-[25%] h-1.5 w-1.5",
  "right-[38%] bottom-[30%] h-1.5 w-1.5",
] as const;

function splitAtMidpoint(text: string): string[] {
  const sentences = text.match(/[^.!?]+[.!?]+/g);
  if (!sentences || sentences.length <= 1) return [text];

  const mid = Math.ceil(sentences.length / 2);
  return [
    sentences.slice(0, mid).join("").trim(),
    sentences.slice(mid).join("").trim(),
  ];
}

export function ComparisonHero({
  trustBadge,
  title,
  titleHighlight,
  description,
  competitorLogo,
}: ComparisonHeroProps) {
  const titleParts = title.split(titleHighlight);

  return (
    <section className="relative overflow-hidden bg-ash-100 pt-32 pb-20 md:pt-40 md:pb-28">
      {DOT_POSITIONS.map((pos, i) => (
        <span key={i} className={`absolute hidden rounded-full bg-rebolt-500/25 md:block ${pos}`} />
      ))}

      <div className="absolute left-8 top-1/3 hidden md:block lg:left-16">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white shadow-md lg:h-16 lg:w-16">
          <Image
            src="/competitors/rebolt-icon.png"
            alt="Rebolt"
            width={40}
            height={40}
            className="h-8 w-8 object-contain lg:h-10 lg:w-10"
          />
        </div>
      </div>

      {competitorLogo && (
        <div className="absolute right-8 top-1/4 hidden md:block lg:right-16">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white shadow-md lg:h-16 lg:w-16">
            <Image
              src={competitorLogo.src}
              alt={competitorLogo.alt}
              width={40}
              height={40}
              className="h-8 w-8 object-contain lg:h-10 lg:w-10"
            />
          </div>
        </div>
      )}

      <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
        <p className="mb-6 inline-block rounded-full bg-rebolt-500/10 px-4 py-1.5 text-sm font-semibold tracking-wide text-rebolt-600">
          {trustBadge}
        </p>

        <h1 className="font-clash text-4xl font-medium leading-[1.05] tracking-snug text-ash-900 md:text-6xl">
          {titleParts[0]}
          <span className="text-rebolt-500">{titleHighlight}</span>
          {titleParts[1]}
        </h1>

        <div className="mx-auto mt-6 max-w-2xl space-y-4 text-lg leading-relaxed text-ash-500 md:text-xl">
          {splitAtMidpoint(description).map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>

        <div className="mt-8">
          <Link
            href="https://cal.com/team/builtright/launch"
            className="inline-flex items-center rounded-full bg-rebolt-500 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-rebolt-600"
          >
            Get More Business
          </Link>
        </div>
      </div>
    </section>
  );
}
